import React, { useState } from 'react';
import WorkTasksModule from './Work/WorkTasksModule';
import SpaceSlot from './SpaceSlot';
import PropertyEditor from './Properties/PropertyEditor';
import { TaskServiceProvider } from '../../contexts/TaskServiceContext';
import { PropertyDefsProvider } from '../../contexts/PropertyDefsContext';
import { ProjectServiceProvider } from '../../contexts/ProjectServiceContext';

/* The body of any private space that runs the real task machinery: Personal,
   Owners, Barbara. (Was PersonalSpaceBody until Owners and Barbara wanted the
   same thing — the only per-space difference is which service is bound and
   whether the space carries extra slots.)

   THE PROVIDER IS THE ENTIRE MOUNT. WorkTasksModule and everything it renders
   (TaskDetail, TaskForm) read their service from TaskServiceContext, whose
   default is Work. Wrapping this subtree in a provider bound to a space's
   service points those exact components at that space's collection — no fork of
   the module, no second copy to keep in step, and three spaces served by one
   component. Outside this wrapper nothing resolves differently, which is why
   /portal/work is untouched.

   THE MODULE IS MOUNTED UNMODIFIED, DELIBERATELY. It carries the studio's
   vocabulary — "By area" offers musicPrep and billing, "By project" lists studio
   projects, a task can be linked to a student. Those are wrong in every private
   space, and gating them means reaching into an 814-line component that is the
   daily driver for 345 live tasks. Mounting it untouched keeps the Work surface
   PROVABLY unchanged and lets the vocabulary be narrowed as its own pass,
   aimed at what actually gets in the way rather than at a guess. The
   user-defined property engine is what eventually replaces those fields here.

   `slots` render BELOW the tasks. Only Personal has any — its Capture inbox,
   which is a shared read-later library and deliberately NOT a task list (see
   buildCaptureSlots). Owners and Barbara pass none.

   `panels` render between the module and the slots: whole surfaces a space
   owns that are neither tasks nor captures. Today that is Personal's
   Subscriptions cut ledger (its own collection, `personalSubscriptions`), and
   it is passed in from SpaceShell for the same reason the services are — the
   space -> what-it-renders mapping lives in ONE place. */
/* `projectService` is OPTIONAL and defaults to undefined, which the provider
   resolves to the Work binding — the same inherit-the-default shape the task
   context uses. Only Personal passes one today; Owners and Barbara have no
   project collection and `spaceFields` withholds the field from them, so the
   module never subscribes and the inherited default is never read. Passing it
   in from SpaceShell rather than deriving it from `service.space` here keeps
   the space -> service mapping in ONE place. */
function SpaceTaskBody({ service, projectService, slots = [], panels = [] }) {
	const [editingProps, setEditingProps] = useState(false);

	return (
		<PropertyDefsProvider space={service.space}>
			<TaskServiceProvider service={service}>
				<ProjectServiceProvider service={projectService}>
				{/* The Properties affordance lives HERE rather than in the module's
				    own header — that keeps the promise that WorkTasksModule is
				    mounted unmodified, and it is correct besides: user-defined
				    properties are a per-space concept and Work has none. */}
				<div className='psp-bodybar'>
					<button
						type='button'
						className='btn-cyv-ghost psp-bodybar__btn'
						onClick={() => setEditingProps(true)}>
						<i className='fa-sharp fa-solid fa-sliders' />
						Properties
					</button>
				</div>

				<WorkTasksModule />

				{editingProps && <PropertyEditor onClose={() => setEditingProps(false)} />}
				</ProjectServiceProvider>
			</TaskServiceProvider>

			{panels.length > 0 && (
				<div className='psp-slots psp-slots--after-module'>
					{panels.map(({ key, Component }) => (
						<Component key={key} />
					))}
				</div>
			)}

			{slots.length > 0 && (
				<div className='psp-slots psp-slots--after-module'>
					{slots.map((slot) => (
						<SpaceSlot key={slot.key} {...slot} />
					))}
				</div>
			)}
		</PropertyDefsProvider>
	);
}

export default SpaceTaskBody;
